import { FormVmValueType } from '../../../../../infrastructure/form-viewmodel-controller';
import { IAddOrderField, ORDER_SUBTYPE_BY_TYPE } from './add-salesforce-order.fields';
import {
    BILLING_METHOD_KEY,
    BILLING_TIME_SENSITIVITY_KEY,
    CANCELLATION_OTHER_KEY,
    CANCELLATION_REASON_KEY,
    HIGH_PROFILE_ATTENDEE_DETAILS_KEY,
    HIGH_PROFILE_ORDER_KEY,
    HIGH_PROFILE_REASONING_KEY,
    isBillingTimeSensitivityVisible,
    isCancellationOtherVisible,
    isHighProfileAttendeeDetailsVisible,
    isHighProfileReasoningVisible,
} from './order-field-visibility';

export interface IOrderFieldValueStore {
    getValue(key: string): FormVmValueType;
    setValue(key: string, value: FormVmValueType): void;
}

export const applyOrderTypeChange = (store: IOrderFieldValueStore, value: FormVmValueType): void => {
    store.setValue('orderType', value);
    const validSubtypes = ORDER_SUBTYPE_BY_TYPE[value as string] ?? [];
    if (!validSubtypes.includes(store.getValue('orderSubtype') as string)) {
        store.setValue('orderSubtype', '');
    }
};

export const applyCancellationReasonChange = (store: IOrderFieldValueStore, value: FormVmValueType): void => {
    store.setValue(CANCELLATION_REASON_KEY, value);
    if (!isCancellationOtherVisible(value as string)) {
        store.setValue(CANCELLATION_OTHER_KEY, '');
    }
};

export const applyHighProfileReasoningChange = (store: IOrderFieldValueStore, value: FormVmValueType): void => {
    store.setValue(HIGH_PROFILE_REASONING_KEY, value);
    if (!isHighProfileAttendeeDetailsVisible(value as string, store.getValue(HIGH_PROFILE_ORDER_KEY))) {
        store.setValue(HIGH_PROFILE_ATTENDEE_DETAILS_KEY, '');
    }
};

export const applyHighProfileOrderChange = (store: IOrderFieldValueStore, value: FormVmValueType): void => {
    store.setValue(HIGH_PROFILE_ORDER_KEY, value);
    if (!isHighProfileReasoningVisible(value)) {
        store.setValue(HIGH_PROFILE_REASONING_KEY, '');
        store.setValue(HIGH_PROFILE_ATTENDEE_DETAILS_KEY, '');
    }
};

export const applyBillingMethodChange = (store: IOrderFieldValueStore, value: FormVmValueType): void => {
    store.setValue(BILLING_METHOD_KEY, value);
    if (!isBillingTimeSensitivityVisible(value as string)) {
        store.setValue(BILLING_TIME_SENSITIVITY_KEY, '');
    }
};

/**
 * Create Order form keys an Order Template stores.
 * contractNumber is the form key; the blueprint payload stores it as contractId.
 * Account display names are captured through onAccountSelected, not as their own fields.
 */
export const ORDER_TEMPLATE_FORM_FIELD_KEYS: ReadonlySet<string> = new Set([
    'contractNumber',
    'multiEventOrder',
    'parentOrder',
    'requesterContact',
    'corporateRelated',
    'orderType',
    'orderSubtype',
    'eventType',
    'eventTypeClient',
    'channelAgencyReferral',
    'highProfileOrder',
    'highProfileReasoning',
    'highProfileAttendeeDetails',
    'isThisEventRepeatable',
    'accountLegalEntity',
    'hybridEvent',
    'opportunity',
    'hostLob',
    'hostSubLob',
    'noStaffRequired',
    'syncToTeams',
    'billToLegalEntity',
    'billingContact',
    'additionalRecipient1',
    'additionalRecipient2',
    'billingMethod',
    'clientCostCode',
    'secondaryPO',
    'billingTimeSensitivity',
    'billingNotes',
    'description',
]);

/** Read-only, cancellation, post-event, and product fields excluded from the template popup. */
export const EXCLUDED_FROM_ORDER_TEMPLATE_FORM_FIELD_KEYS = [
    'orderStatus',
    'billingStatus',
    'lassoSyncStatus',
    'lassoSyncDatetime',
    'teamsCreated',
    'processedForArchive',
    'billingAddress',
    'billingContactEmail',
    'additionalRecipient1Email',
    'additionalRecipient2Email',
    'feedbackFormUrl',
    'cancellationFee',
    'cancellationReason',
    'cancellationOther',
    'qaChatEnabled',
    'qaReportSent',
    'eventRanSmoothly',
    'postEventNotes',
    'priceBook',
    'currency',
] as const;

export const isOrderTemplateFormField = (key: string): boolean => ORDER_TEMPLATE_FORM_FIELD_KEYS.has(key);

export const orderTemplateFieldsFor = (fields: IAddOrderField[]): IAddOrderField[] =>
    fields.filter(field => isOrderTemplateFormField(field.key));
